// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import { INK, INK_2, WARM } from "./boldHead";

const WIDTH = 150;
const HEIGHT = 126;
const PANEL_HEIGHT = 76;
const CENTRE_X = WIDTH / 2;
const CENTRE_Y = 1 + PANEL_HEIGHT / 2;

// Where the bar under the panel sits. It is below the dark panel with a clear gap, so its label
// is dark ink on the card rather than orange on grey.
const LABEL_Y = 97;
const BAR_Y = 107;

const CROSS_ARM = 9;
const FRAME_SIZE = 40;

// Which eye is shown the frame. `Constants.SubjectiveAngle.EnclosureEye` in the Algorithms
// library, which the exam does not counterbalance -- the frame always goes to the left eye and
// the cross to the right. Named rather than inlined so this figure stays right if that ever
// changes.
export const ENCLOSURE_EYE = "Left";

const directionSign = (direction: string): number =>
  /^(eso|right h|hyper)/i.test(direction) ? 1 : -1;

type SubjectiveFigureType = {
  // Where the patient put the cross, in prism dioptres from straight ahead.
  subjectivePrismDioptres: number;
  // Where the headset measured the eye to be. The gap between the two is the angle of anomaly.
  objectivePrismDioptres: number | null;
  direction: string;
  fixingEye?: "Left" | "Right";
};

/**
 * What the patient was shown and where they put it: a frame to one eye, a cross to the other,
 * and how far from straight ahead the cross ended up.
 *
 * The grey dot on the bar is where the headset measured the eye to be, so correspondence is a
 * gap the reader can see rather than a word they have to look up.
 */
const SubjectiveFigure = ({
  subjectivePrismDioptres,
  objectivePrismDioptres,
  direction,
  fixingEye = "Left",
}: SubjectiveFigureType) => {
  const { t } = useTranslation();

  const sign = directionSign(direction);
  const deviatedSide = fixingEye === "Left" ? -1 : 1;

  // Magnitude only: `direction` already says which way the deviation goes, and the payload's
  // own sign on `prismDioptres` carries the same information. Using both would double it.
  const toX = (prismDioptres: number) =>
    -sign * deviatedSide * Math.min(Math.abs(prismDioptres) * 1.3, 40);

  const crossX = toX(subjectivePrismDioptres);
  const measuredX =
    objectivePrismDioptres === null ? null : toX(objectivePrismDioptres);

  return (
    <Box
      component="svg"
      role="img"
      data-testid="synoptophore-subjective-figure"
      aria-label={t("Where the patient placed the cross")}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      sx={{ display: "block", width: "100%", mx: "auto" }}
    >
      <rect
        x={1}
        y={1}
        width={148}
        height={PANEL_HEIGHT}
        rx={6}
        fill="#0B0D14"
      />

      <line
        x1={CENTRE_X}
        y1={CENTRE_Y - 29}
        x2={CENTRE_X}
        y2={CENTRE_Y + 29}
        stroke="#FFFFFF"
        strokeOpacity={0.16}
        strokeDasharray="3 3"
      />

      {/* The frame, shown to the enclosure eye and never moved by the patient. */}
      <rect
        x={CENTRE_X - FRAME_SIZE / 2}
        y={CENTRE_Y - FRAME_SIZE / 2}
        width={FRAME_SIZE}
        height={FRAME_SIZE}
        rx={3}
        fill="none"
        stroke="#9FB4C9"
        strokeWidth={2.4}
      />

      {/* The cross, shown to the other eye, where the patient left it. */}
      <path
        d={`M${CENTRE_X + crossX - CROSS_ARM} ${CENTRE_Y} L${
          CENTRE_X + crossX + CROSS_ARM
        } ${CENTRE_Y} M${CENTRE_X + crossX} ${CENTRE_Y - CROSS_ARM} L${
          CENTRE_X + crossX
        } ${CENTRE_Y + CROSS_ARM}`}
        stroke={WARM}
        strokeWidth={3}
        strokeLinecap="round"
      />

      {Math.abs(crossX) > 0.5 && (
        <>
          <line
            x1={CENTRE_X}
            y1={BAR_Y}
            x2={CENTRE_X + crossX}
            y2={BAR_Y}
            stroke={WARM}
            strokeWidth={2.4}
            strokeLinecap="round"
          />
          <line
            x1={CENTRE_X}
            y1={BAR_Y - 4}
            x2={CENTRE_X}
            y2={BAR_Y + 4}
            stroke={INK_2}
            strokeWidth={2}
          />
        </>
      )}

      {measuredX !== null && (
        <circle cx={CENTRE_X + measuredX} cy={BAR_Y} r={3.4} fill={INK_2} />
      )}

      <text
        x={CENTRE_X + crossX / 2}
        y={LABEL_Y}
        fontSize="10"
        fontWeight={600}
        fill={INK}
        textAnchor="middle"
      >
        {`${Math.abs(subjectivePrismDioptres).toFixed(1)}Δ`}
      </text>

      <text
        x={CENTRE_X + (crossX < 0 ? 4 : -4)}
        y={BAR_Y + 14}
        fontSize="8"
        fill={INK_2}
        textAnchor={crossX < 0 ? "start" : "end"}
      >
        {t("straight ahead")}
      </text>
    </Box>
  );
};

export default SubjectiveFigure;
